import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { customersApiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import './MyTickets.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const Tickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const navigate = useNavigate();

  // Filter state
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');

  // Search state
  const [searchCustomerName, setSearchCustomerName] = useState('');
  const [searchCustomerNumber, setSearchCustomerNumber] = useState('');
  const [searchTicketNumber, setSearchTicketNumber] = useState('');

  const handleGoToTicket = id => {
    navigate(`/smartTicket/${id}`);
  };

  const handleDeleteTicket = async ticketId => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await customersApiService.deleteTicket(ticketId);
        setTickets(tickets.filter(ticket => ticket.id !== ticketId));
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const fetchTickets = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }
      const response = await customersApiService.getAllTickets();

      // Handle response - could be array or object with data property
      let allTickets = [];
      if (Array.isArray(response)) {
        allTickets = response;
      } else if (response && Array.isArray(response.data)) {
        allTickets = response.data;
      } else if (response && Array.isArray(response.tickets)) {
        allTickets = response.tickets;
      } else {
        console.warn('Unexpected response format from getAllTickets:', response);
      }

      // Filter tickets by user_id to show only tickets for this user
      const ticketsData = allTickets.filter(ticket => String(ticket.user_id) === String(user?.id));
      setTickets(ticketsData);
      setLastSynced(new Date());
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      if (isRefresh) {
        setRefreshing(false);
      }
    }
  }, [user?.id]);

  const handleRefresh = () => {
    fetchTickets(true);
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Auto-refresh when page becomes visible again (syncs with customers portal)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTickets(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchTickets]);

  // Derive available years from all tickets
  const availableYears = useMemo(() => {
    const years = new Set(
      tickets
        .filter(t => t.updated_at)
        .map(t => {
          // Parse date and extract year (handles ISO strings correctly)
          const date = new Date(t.updated_at);
          return date.getFullYear();
        })
    );
    return [...years].sort((a, b) => b - a);
  }, [tickets]);

  // Derive available months for the selected year
  const availableMonths = useMemo(() => {
    if (!filterYear) return [];
    const months = new Set(
      tickets
        .filter(t => t.updated_at && new Date(t.updated_at).getFullYear() === Number(filterYear))
        .map(t => new Date(t.updated_at).getMonth())
    );
    return [...months].sort((a, b) => a - b);
  }, [tickets, filterYear]);

  // Apply filters to produce displayed tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      // Filter by date
      if (!ticket.updated_at) return !filterYear;
      const d = new Date(ticket.updated_at);
      if (filterYear && d.getFullYear() !== Number(filterYear)) return false;
      if (filterMonth !== '' && d.getMonth() !== Number(filterMonth)) return false;
      return true;
    });
  }, [tickets, filterYear, filterMonth]);

  const handleYearChange = e => {
    setFilterYear(e.target.value);
    setFilterMonth('');
  };

  const handleMonthChange = e => {
    setFilterMonth(e.target.value);
  };

  const handleClearFilters = () => {
    setFilterYear('');
    setFilterMonth('');
  };

  const handleSearch = e => {
    e.preventDefault();
    // Check if at least one search field has a value
    if (!searchCustomerName.trim() && !searchCustomerNumber.trim() && !searchTicketNumber.trim()) {
      return;
    }
    // Build query parameters
    const params = new URLSearchParams();
    if (searchCustomerName.trim()) params.append('customer_name', searchCustomerName);
    if (searchCustomerNumber.trim()) params.append('customer_number', searchCustomerNumber);
    if (searchTicketNumber.trim()) params.append('ticket_number', searchTicketNumber);
    navigate(`/search/search?${params.toString()}`);
  };

  const isFiltered = filterYear !== '' || filterMonth !== '';

  if (loading)
    return <p className="text-center text-gray-500">Loading tickets...</p>;
  if (error) return <p className="text-center text-red-500">Error: {error}</p>;

  return (
    <div className="p-6 bg-slate-100 min-h-screen">
      {/* Search and Filter Cards - Responsive Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Search Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Search</h2>
          <form onSubmit={handleSearch}>
            <div className="flex flex-wrap gap-3 items-end">
              {/* Customer Name */}
              <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                <label htmlFor="search-customer-name" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer Name</label>
                <input
                  id="search-customer-name"
                  type="text"
                  value={searchCustomerName}
                  onChange={e => setSearchCustomerName(e.target.value)}
                  placeholder="Customer Name"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>

              {/* Customer Number */}
              <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                <label htmlFor="search-customer-number" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer Number</label>
                <input
                  id="search-customer-number"
                  type="text"
                  value={searchCustomerNumber}
                  onChange={e => setSearchCustomerNumber(e.target.value)}
                  placeholder="Customer Number"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>

              {/* Ticket Number */}
              <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                <label htmlFor="search-ticket-number" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ticket Number</label>
                <input
                  id="search-ticket-number"
                  type="text"
                  value={searchTicketNumber}
                  onChange={e => setSearchTicketNumber(e.target.value)}
                  placeholder="Ticket Number"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>

              {/* Search Button */}
              <button
                type="submit"
                className="px-4 py-2 bg-slate-600 hover:bg-green-500 text-white font-medium rounded-md border-b-4 border-slate-800 hover:border-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                </svg>
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Filter Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Filter</h2>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Sync Button */}
              <div className="flex items-center gap-2 mt-5">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium rounded-md border border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sync with customers portal"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`size-4 ${refreshing ? 'animate-spin' : ''}`}
                  >
                    <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
                  </svg>
                  {refreshing ? 'Syncing...' : 'Sync'}
                </button>
                {lastSynced && (
                  <span className="text-xs text-gray-400" title={lastSynced.toLocaleString()}>
                    {lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              {/* Year */}
              <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                <label htmlFor="filter-year" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Year</label>
                <select
                  id="filter-year"
                  value={filterYear}
                  onChange={handleYearChange}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer"
                >
                  <option value="">All years</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
                <label htmlFor="filter-month" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Month</label>
                <select
                  id="filter-month"
                  value={filterMonth}
                  onChange={handleMonthChange}
                  disabled={!filterYear}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <option value="">All months</option>
                  {availableMonths.map(month => (
                    <option key={month} value={month}>{MONTH_NAMES[month]}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results count + Clear */}
            <div className="flex items-center gap-3">
              {isFiltered && (
                <span className="text-sm text-gray-500">
                  <span className="font-semibold text-slate-700">{filteredTickets.length}</span> of {tickets.length} tickets
                </span>
              )}
              {isFiltered && (
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium rounded-md border border-slate-300 transition-colors ml-auto"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Grid */}
      {filteredTickets.length === 0 ? (
        <p className="text-center text-gray-500">
          {isFiltered ? 'No tickets match the selected filters.' : 'You have no registered tickets.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTickets.map(ticket => (
            <div
              key={ticket.ticket_number || Math.random()}
              className="bg-white p-4 shadow-lg rounded-lg border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Ticket #{ticket.ticket_number.toUpperCase()}
              </h3>

              <p className="text-sm text-gray-600 mt-2">
                Customer:{' '}
                <span className="font-medium">{ticket.customer_name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Customer Number:{' '}
                <span className="font-medium">{ticket.customer_number}</span>
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Issue With:{' '}
                <span className="font-medium text-red-500">
                  {ticket.issue_with}
                </span>
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/smart-ticket/${ticket.id}`)}
                  className="px-4 py-2 mt-2 bg-blue-600 text-white font-semibold border-b-4 rounded hover:bg-blue-500 border-blue-800 hover:border-blue-700 flex items-center"
                >
                  Go to ticket
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                <button
                  onClick={() => handleDeleteTicket(ticket.id)}
                  className="px-8 py-2 mt-2 ml-auto bg-red-900 hover:bg-red-800 text-white font-semibold border-b-4 rounded border-red-800 hover:border-red-700 flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Tickets;
